import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import './Notifications.css';

const Notifications = () => {
  const { notifications, setNotifications, hearings, judgments, cases, addAuditLog } = useApp();
  const [filterType, setFilterType] = useState('All');

  useEffect(() => {
    // Auto-generate notifications for upcoming hearings
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const upcomingHearings = hearings.filter(h => {
      const hearingDate = new Date(h.hearingDate);
      return hearingDate >= today && hearingDate <= tomorrow && h.status === 'Scheduled';
    });

    upcomingHearings.forEach(hearing => {
      const exists = notifications.some(n => 
        n.type === 'Hearing Reminder' && 
        n.referenceId === hearing.id
      );
      
      if (!exists) {
        const newNotification = {
          id: Date.now().toString() + Math.random(),
          type: 'Hearing Reminder',
          title: `Hearing Reminder: ${hearing.caseNumber}`,
          message: `Hearing scheduled for ${new Date(hearing.hearingDate).toLocaleDateString()} at ${hearing.hearingTime}`,
          referenceId: hearing.id,
          caseNumber: hearing.caseNumber,
          isRead: false,
          createdAt: new Date().toISOString(),
        };
        setNotifications(prev => [...prev, newNotification]);
      }
    });

    // Auto-generate notifications for new judgments
    judgments.forEach(judgment => {
      const exists = notifications.some(n => 
        n.type === 'Judgment Available' && 
        n.referenceId === judgment.id
      );
      
      if (!exists) {
        const newNotification = {
          id: Date.now().toString() + Math.random(),
          type: 'Judgment Available',
          title: `Judgment Available: ${judgment.caseNumber}`,
          message: `${judgment.judgmentType} available for case ${judgment.caseNumber}`,
          referenceId: judgment.id,
          caseNumber: judgment.caseNumber,
          isRead: false,
          createdAt: new Date().toISOString(),
        };
        setNotifications(prev => [...prev, newNotification]);
      }
    });
  }, [hearings, judgments, notifications, setNotifications]);

  const filteredNotifications = filterType === 'All' 
    ? notifications 
    : notifications.filter(n => n.type === filterType);

  const unreadCount = notifications.filter(n => !n.isRead).length;

  const markAsRead = (id) => {
    setNotifications(notifications.map(n => 
      n.id === id ? { ...n, isRead: true } : n
    ));
  };

  const markAllAsRead = () => {
    setNotifications(notifications.map(n => ({ ...n, isRead: true })));
    addAuditLog('Notifications', 'All notifications marked as read');
  };

  const deleteNotification = (id) => {
    setNotifications(notifications.filter(n => n.id !== id));
  };

  const getNotificationIcon = (type) => {
    switch(type) {
      case 'Hearing Reminder': return '📅';
      case 'Judgment Available': return '⚖️';
      case 'Case Update': return '📁';
      default: return '🔔';
    }
  };

  return (
    <div className="notifications-container">
      <div className="notifications-header">
        <div>
          <h1>🔔 Notifications</h1>
          {unreadCount > 0 && (
            <span className="unread-badge">{unreadCount} unread</span>
          )}
        </div>
        {unreadCount > 0 && (
          <button onClick={markAllAsRead} className="btn-secondary">
            Mark All as Read
          </button>
        )}
      </div>

      <div className="notifications-filters">
        <button
          className={filterType === 'All' ? 'filter-active' : 'filter-btn'}
          onClick={() => setFilterType('All')}
        >
          All ({notifications.length})
        </button>
        <button
          className={filterType === 'Hearing Reminder' ? 'filter-active' : 'filter-btn'}
          onClick={() => setFilterType('Hearing Reminder')}
        >
          Hearing Reminders ({notifications.filter(n => n.type === 'Hearing Reminder').length})
        </button>
        <button
          className={filterType === 'Judgment Available' ? 'filter-active' : 'filter-btn'}
          onClick={() => setFilterType('Judgment Available')}
        >
          Judgments ({notifications.filter(n => n.type === 'Judgment Available').length})
        </button>
        <button
          className={filterType === 'Case Update' ? 'filter-active' : 'filter-btn'}
          onClick={() => setFilterType('Case Update')}
        >
          Case Updates ({notifications.filter(n => n.type === 'Case Update').length})
        </button>
      </div>

      <div className="notifications-list">
        {filteredNotifications.length > 0 ? (
          filteredNotifications
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .map(notification => (
              <div
                key={notification.id}
                className={`notification-card ${!notification.isRead ? 'unread' : ''}`}
              >
                <div className="notification-icon">
                  {getNotificationIcon(notification.type)}
                </div>
                <div className="notification-content">
                  <div className="notification-header">
                    <h3>{notification.title}</h3>
                    <span className="notification-time">
                      {new Date(notification.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <p className="notification-message">{notification.message}</p>
                  {notification.caseNumber && (
                    <p className="notification-case">
                      Case: <strong>{notification.caseNumber}</strong>
                    </p>
                  )}
                </div>
                <div className="notification-actions">
                  {!notification.isRead && (
                    <button
                      onClick={() => markAsRead(notification.id)}
                      className="btn-mark-read"
                    >
                      Mark as Read
                    </button>
                  )}
                  <button
                    onClick={() => deleteNotification(notification.id)}
                    className="btn-delete"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
        ) : (
          <div className="empty-state">
            <p>No notifications found.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;

